import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/api';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await adminService.getDashboardStatistics();
        setStatistics(data);
      } catch (err) {
        console.error('Error fetching dashboard statistics:', err);
        setError('Failed to load dashboard statistics. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-card">
          <div className="card-content" style={{ textAlign: 'center' }}>
            Loading dashboard...
          </div>
        </div>
      </div>
    );
  }

  if (error || !statistics) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-card">
          <div className="card-content" style={{ textAlign: 'center' }}>
            {error || 'No dashboard data available.'}
          </div>
        </div>
      </div>
    );
  }

  const stats = [
    { label: 'Total Users', value: statistics.totalUsers ?? 0, path: '/admin/users' },
    { label: 'Active Users', value: statistics.activeUsers ?? 0, path: '/admin/users' },
    { label: 'Hours This Month', value: statistics.totalHoursThisMonth ?? 0, path: '/admin/time-reports' },
    { label: 'Pending Approvals', value: statistics.pendingApprovals ?? 0, path: '/admin/time-reports' }
  ];

  const hoursPerDay = statistics.hoursPerDay || [];
  const maxDayHours = Math.max(...hoursPerDay.map(d => d.hours ?? 0), 0);

  const recentEntries = statistics.recentEntries || [];

  // Approval status distribution derived from the recent entries
  const statusCounts = recentEntries.reduce(
    (acc, entry) => {
      if (acc[entry.status] !== undefined) {
        acc[entry.status] += 1;
      }
      return acc;
    },
    { approved: 0, pending: 0, challenged: 0 }
  );
  const statusTotal = statusCounts.approved + statusCounts.pending + statusCounts.challenged;
  const statusPercent = (count) =>
    statusTotal > 0 ? Math.round((count / statusTotal) * 100) : 0;

  return (
    <div className="dashboard-container">
      {/* Statistics */}
      <div className="stats-grid">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="stat-card"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(stat.path)}
          >
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="dashboard-grid">
        <div className="grid-item-7">
          <div className="dashboard-card">
            <div className="card-header">
              <div>
                <h3 className="card-title">Time Entries by Day</h3>
                <div className="card-subtitle">Current Week</div>
              </div>
              <button className="action-button" onClick={() => navigate('/admin/time-reports')}>
                View All
              </button>
            </div>

            <div className="card-content">
              <div className="chart-container">
                <div className="chart-bars">
                  {hoursPerDay.map((data, index) => (
                    <div
                      key={index}
                      className="chart-bar"
                      style={{
                        height: `${maxDayHours > 0 ? ((data.hours ?? 0) / maxDayHours) * 100 : 0}%`,
                        '--day-label': `"${data.day}"`
                      }}
                      title={`${data.day}: ${data.hours ?? 0} hours`}
                      data-day={data.day}
                    ></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid-item-5">
          <div className="dashboard-card">
            <div className="card-header">
              <div>
                <h3 className="card-title">Approval Status</h3>
                <div className="card-subtitle">Recent Entries</div>
              </div>
            </div>

            <div className="card-content">
              <div className="chart-container">
                <div className="status-chart">
                  <div className="status-row">
                    <div className="status-label">Approved</div>
                    <div className="status-bar-container">
                      <div className="status-bar-bg">
                        <div className="status-bar approved" style={{ width: `${statusPercent(statusCounts.approved)}%` }}></div>
                      </div>
                      <div className="status-value">{statusPercent(statusCounts.approved)}%</div>
                    </div>
                  </div>

                  <div className="status-row">
                    <div className="status-label">Pending</div>
                    <div className="status-bar-container">
                      <div className="status-bar-bg">
                        <div className="status-bar pending" style={{ width: `${statusPercent(statusCounts.pending)}%` }}></div>
                      </div>
                      <div className="status-value">{statusPercent(statusCounts.pending)}%</div>
                    </div>
                  </div>

                  <div className="status-row">
                    <div className="status-label">Challenged</div>
                    <div className="status-bar-container">
                      <div className="status-bar-bg">
                        <div className="status-bar challenged" style={{ width: `${statusPercent(statusCounts.challenged)}%` }}></div>
                      </div>
                      <div className="status-value">{statusPercent(statusCounts.challenged)}%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent time entries */}
      <div className="dashboard-card">
        <div className="card-header">
          <div>
            <h3 className="card-title">Recent Time Entries</h3>
            <div className="card-subtitle">All customers</div>
          </div>
          <button className="action-button" onClick={() => navigate('/admin/time-reports')}>
            View All
          </button>
        </div>

        <div className="card-content">
          <div className="table-container">
            <table className="data-table">
              <thead className="table-head">
                <tr>
                  <th className="table-head-cell">Employee</th>
                  <th className="table-head-cell">Customer</th>
                  <th className="table-head-cell">Date</th>
                  <th className="table-head-cell">Hours</th>
                  <th className="table-head-cell">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentEntries.length > 0 ? (
                  recentEntries.map((entry) => (
                    <tr key={entry.id} className="table-row">
                      <td className="table-cell">{entry.employeeName}</td>
                      <td className="table-cell">{entry.customerName}</td>
                      <td className="table-cell">{entry.date}</td>
                      <td className="table-cell">{entry.hours}</td>
                      <td className="table-cell">
                        <span className={`status-badge ${entry.status}`}>
                          {entry.status
                            ? entry.status.charAt(0).toUpperCase() + entry.status.slice(1)
                            : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="table-row">
                    <td className="table-cell" colSpan={5} style={{ textAlign: 'center' }}>
                      No recent time entries.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
